import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import AdminLayout from "../../platform/AdminLayout";
import { api, downloadCsv } from "../../lib/api";
import { PeriodPicker, Bars, StatCard, dayPlus } from "../../components/stats";
import { ButtonIcon } from "../../components/PagePreview";

export default function AdminDashboard() {
  const [from, setFrom] = useState(dayPlus(-6));
  const [to, setTo] = useState(dayPlus(0));
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    api(`/admin/stats?date_from=${from}&date_to=${to}`).then(setStats).catch((e) => setError(e.message));
  }, [from, to]);

  useEffect(load, [load]);

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-nuit-900 sm:text-3xl">Tableau de bord</h1>
          <p className="mt-1 text-sm text-steel">
            Consultation = ouverture d'une page. Clic = clic sur un bouton (pas une action accomplie :
            un clic Google Avis ne garantit pas un avis publié).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadCsv("/admin/clients-export.csv", "clients-noveocard.csv")}
            data-testid="export-clients-btn"
            className="flex items-center gap-1.5 rounded-full border border-nuit-900/15 bg-white px-4 py-2.5 font-display text-xs font-semibold text-nuit-900 hover:border-nuit-900/40">
            <Download className="h-3.5 w-3.5" /> Clients (CSV)
          </button>
          <button onClick={() => downloadCsv(`/admin/stats-export.csv?date_from=${from}&date_to=${to}`, "statistiques-noveocard.csv")}
            data-testid="export-stats-btn"
            className="flex items-center gap-1.5 rounded-full bg-nuit-900 px-4 py-2.5 font-display text-xs font-semibold text-white hover:bg-nuit-800">
            <Download className="h-3.5 w-3.5" /> Statistiques (CSV)
          </button>
        </div>
      </div>

      <div className="mt-6">
        <PeriodPicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      </div>

      {error && <p className="mt-6 rounded-xl bg-red-50 p-4 text-sm font-medium text-red-700" role="alert">{error}</p>}

      {stats && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="stats-cards">
            <StatCard label="Consultations" value={stats.views} sub={`du ${stats.from} au ${stats.to}`} />
            <StatCard label="Clics sur boutons" value={stats.clicks} sub="tous clients confondus" />
            <StatCard label="Clients actifs" value={stats.clients.actif || 0} sub={`${stats.clients.inactif || 0} inactif(s) · ${stats.clients.archive || 0} archivé(s)`} />
            <StatCard label="Bouton le plus utilisé" value={stats.per_button[0]?.label || "—"} sub={stats.per_button[0] ? `${stats.per_button[0].clicks} clics` : "aucun clic sur la période"} />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-[1.5fr_1fr]">
            <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
              <h2 className="font-display text-base font-bold text-nuit-900">Évolution</h2>
              <div className="mt-4"><Bars series={stats.series} /></div>
            </section>
            <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
              <h2 className="font-display text-base font-bold text-nuit-900">Classement des boutons</h2>
              <ul className="mt-4 space-y-2.5">
                {stats.per_button.length === 0 && <li className="text-sm text-steel">Aucun clic enregistré.</li>}
                {stats.per_button.map((b) => (
                  <li key={b.button} className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2.5 font-medium text-nuit-900">
                      <ButtonIcon icon={({ star: "star" }[b.button]) || "link"} className="h-4 w-4 text-electric" />
                      {b.label}
                    </span>
                    <span className="font-display font-bold text-electric">{b.clicks}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section className="mt-6 rounded-3xl border border-nuit-900/10 bg-white p-6">
            <h2 className="font-display text-base font-bold text-nuit-900">Classement des pages</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-nuit-900/10 text-left text-xs uppercase tracking-wider text-steel">
                    <th className="pb-2.5 pr-4 font-semibold">Commerce</th>
                    <th className="pb-2.5 pr-4 font-semibold">Consultations</th>
                    <th className="pb-2.5 pr-4 font-semibold">Clics</th>
                    <th className="pb-2.5 font-semibold">Page</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_pages.length === 0 && (
                    <tr><td colSpan={4} className="py-5 text-center text-steel">Aucune consultation sur la période.</td></tr>
                  )}
                  {stats.top_pages.map((p) => (
                    <tr key={p.client_id} className="border-b border-nuit-900/5 last:border-0">
                      <td className="py-3 pr-4 font-semibold text-nuit-900">
                        <Link to={`/admin/clients/${p.client_id}`} className="hover:text-electric">{p.business_name}</Link>
                      </td>
                      <td className="py-3 pr-4">{p.views}</td>
                      <td className="py-3 pr-4">{p.clicks}</td>
                      <td className="py-3">
                        <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-electric hover:underline">
                          /p/{p.slug} <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </AdminLayout>
  );
}
